import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../db/indexedDb';
import { SearchBar } from '../components/SearchBar';
import { SongList } from '../components/SongList';
import { usePlaylist, useTracks } from '../hooks/useIndexedDb';
import { usePlayer } from '../hooks/usePlayer';
import { useToast } from '../hooks/useToast';
import type { Track } from '../types';

const EMPTY_TRACKS: Track[] = [];

function SortablePill({ id, label }: { id: string; label: string }) {
  const { setNodeRef, listeners, attributes, transform, transition } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="pill cursor-grab"
      {...attributes}
      {...listeners}
    >
      {label}
    </div>
  );
}

export function PlaylistDetailPage() {
  const { playlistId } = useParams();
  const playlist = usePlaylist(playlistId);
  const trackQuery = useTracks();
  const tracks = trackQuery ?? EMPTY_TRACKS;
  const { enqueue, playTrack } = usePlayer();
  const { pushToast } = useToast();
  const [query, setQuery] = useState('');
  const sensors = useSensors(useSensor(PointerSensor));

  const playlistTracks = useMemo(() => {
    const map = new Map(tracks.map((track) => [track.id, track]));
    return (playlist?.trackIds ?? [])
      .map((trackId) => map.get(trackId))
      .filter((track): track is Track => Boolean(track))
      .filter((track) =>
        [track.title, track.artist, track.album].some((field) =>
          field.toLowerCase().includes(query.toLowerCase())
        )
      );
  }, [playlist?.trackIds, query, tracks]);

  if (!playlist) {
    return (
      <div className="panel rounded-[2rem] p-8">
        <p className="text-sm text-foreground/70">Playlist not found.</p>
        <Link to="/playlists" className="mt-4 inline-flex rounded-full bg-accent px-4 py-2 font-semibold text-black">
          Back to playlists
        </Link>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <div className="panel rounded-[2rem] p-6">
        <Link to="/playlists" className="text-sm text-accent">
          Back to playlists
        </Link>
        <h2 className="mt-4 font-display text-4xl font-bold">{playlist.name}</h2>
        <p className="mt-2 text-sm text-foreground/70">
          Drag to reorder below. Changes are persisted locally right away.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              enqueue(playlist.trackIds);
              pushToast({ title: 'Playlist queued', description: `${playlist.trackIds.length} tracks added.` });
            }}
            className="rounded-full bg-accent px-4 py-2 font-semibold text-black"
          >
            Add all to queue
          </button>
          <button
            type="button"
            onClick={() => void playTrack(playlist.trackIds[0], playlist.trackIds)}
            className="pill"
            disabled={!playlist.trackIds.length}
          >
            Play playlist
          </button>
        </div>
      </div>

      <div className="panel rounded-[2rem] p-6">
        <SearchBar value={query} onChange={setQuery} placeholder="Search inside playlist" />
        <div className="mt-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => {
              const { active, over } = event;
              if (!over || active.id === over.id) return;
              const oldIndex = playlist.trackIds.indexOf(String(active.id));
              const newIndex = playlist.trackIds.indexOf(String(over.id));
              const trackIds = arrayMove(playlist.trackIds, oldIndex, newIndex);
              void db.updatePlaylist({ ...playlist, trackIds });
            }}
          >
            <SortableContext items={playlist.trackIds} strategy={verticalListSortingStrategy}>
              <div className="mb-6 flex flex-wrap gap-2">
                {playlist.trackIds.map((trackId) => {
                  const track = tracks.find((item) => item.id === trackId);
                  return track ? <SortablePill key={trackId} id={trackId} label={track.title} /> : null;
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
        <SongList
          tracks={playlistTracks}
          onPlay={(trackId) => void playTrack(trackId, playlist.trackIds)}
          onQueue={(trackId) => enqueue([trackId])}
        />
      </div>
    </section>
  );
}
